/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NgForm} from '@angular/forms';
import {Subscription} from 'rxjs';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import template from './network-page.component.html';

import {AdServerConfig} from '../../../components/api/api';
import {CurrenciesResource} from '../../../components/api/resources/resource/currencies';
import {formatAdnStat} from '../../common/filters/format-adn-stat';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {
  DEFAULT_BID_SPECIFICATION_FIELDS,
  DEFAULT_MARKETPLACE_BID_SPECIFICATION_FIELDS,
  DEFAULT_MARKETPLACE_OBJECTIVE_FIELDS,
  DEFAULT_OBJECTIVE_FIELDS
} from '../../../components/session/local-network-profile';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  hasFormValidationError,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../../common/controller/form-submit-state';
import {MarketplaceProductResource} from '../../../components/api/resources/resource/marketplace-product.resource';
import {NetworkResource} from '../../../components/api/resources/resource/network.resource';
import {NetworkProfileResource} from '../../../components/api/resources/resource/network-profile.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {TierResource} from '../../../components/api/resources/resource/tier';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-network-page',
  template
})
export class NetworkPageComponent implements OnInit, OnDestroy {
  readonly defaultObjectiveFieldKeys = Object.keys(DEFAULT_OBJECTIVE_FIELDS);
  readonly defaultMarketplaceObjectiveFieldKeys = Object.keys(DEFAULT_MARKETPLACE_OBJECTIVE_FIELDS);
  readonly defaultBidSpecificationFieldKeys = Object.keys(DEFAULT_BID_SPECIFICATION_FIELDS);
  readonly defaultMarketplaceBidSpecificationFieldKeys = Object.keys(DEFAULT_MARKETPLACE_BID_SPECIFICATION_FIELDS);
  readonly runReportsOptions = ['REACH_ANALYSIS', 'ADVERTISER_REPORTS'];

  network: any;
  networkProfile: any;
  originalNetwork: any;
  originalNetworkProfile: any;
  app: string;
  currencies: string[] = [];
  tiers: any[] | null = null;
  marketplaceProducts: any[] = [];
  isPureMarketplace = false;
  sftpHost = '';
  startDays = [
    {value: 0, text: 'Sunday'},
    {value: 1, text: 'Monday'},
    {value: 2, text: 'Tuesday'},
    {value: 3, text: 'Wednesday'},
    {value: 4, text: 'Thursday'},
    {value: 5, text: 'Friday'},
    {value: 6, text: 'Saturday'}
  ];

  submitState: FormSubmitState = createFormSubmitState();
  pending = false;
  namedLocationRowIds: number[] = [];

  private _registeredForm: NgForm | null = null;
  private _angularFormDirty = false;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _initializingForm = false;
  private _nextNamedLocationRowId = 0;
  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    private readonly eventBus: AppEventBusService,
    private readonly translate: TranslateService,
    @Inject(NetworkResource) private readonly Network: any,
    @Inject(NetworkProfileResource) private readonly NetworkProfile: any,
    @Inject(MarketplaceProductResource) private readonly MarketplaceProduct: any,
    private readonly currenciesResource: CurrenciesResource,
    @Inject(TierResource) private readonly tierResource: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly ngZone: NgZone,
    private readonly appLocale: AppLocaleService,
    private readonly cdr: ChangeDetectorRef
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState, {
      getRegisteredForm: () => this._registeredForm
    });
  }

  ngOnInit(): void {
    this.app = this.localUserProfile.getCurrentApp();
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();

    const env = AdServerConfig.obtain().getEnv();
    const networkTag = this.LocalNetworkProfile.getNetworkTag();
    this.sftpHost = env !== 'production'
      ? `${networkTag}.${env}.data.adnuntius.com:8164`
      : `${networkTag}.data.adnuntius.com:8164`;

    Promise.all([
      this.Network.get({id: this.LocalNetworkProfile.getNetworkId()}).$promise,
      this.NetworkProfile.get({id: this.LocalNetworkProfile.getNetworkId()}).$promise
    ]).then(([network, networkProfile]) => {
      this.ngZone.run(() => {
        this.originalNetwork = network;
        this.originalNetworkProfile = networkProfile;
        this.initModels();
        this.loadSupportingData();
      });
    });
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
  }

  registerForm(editForm: NgForm): string {
    if (this._registeredForm !== editForm) {
      this._registeredForm = editForm;
      this._startTrackingForm();
    }
    return '';
  }

  submit(editForm: NgForm): void {
    if (this.pending || this.isSaveDisabled(editForm)) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);

    this.Network.save(this.network, (savedNetwork: any) => {
      this.ngZone.run(() => {
        const network = savedNetwork && savedNetwork.defaultCurrency ? savedNetwork : this.network;

        this.NetworkProfile.save({
          id: network.id,
          name: this.network.name,
          profile: {
            adnUiLogo: this.networkProfile.profile.adnUiLogo,
            adnDefaults: this.networkProfile.profile.adnDefaults
          }
        }, (savedNetworkProfile: any) => {
          this.ngZone.run(() => {
            this.originalNetwork = _.cloneDeep(network);
            this.originalNetworkProfile = _.cloneDeep(savedNetworkProfile);
            this.initModels();
            updateFormWarnings(this.submitState, network, this.LocalNetworkProfile.isPureMarketplacePlatform());

            this.LocalNetworkProfile.set('adnDefaults', savedNetworkProfile.profile.adnDefaults);
            this.LocalNetworkProfile.set('adnUiLogo', savedNetworkProfile.profile.adnUiLogo);
            this.LocalNetworkProfile.set('reportLogoUrl', network.reportLogoUrl);
            this.LocalNetworkProfile.set('availableCurrencies', network.availableCurrencies);
            this.LocalNetworkProfile.set('defaultCurrency', network.defaultCurrency);
            this.LocalNetworkProfile.set('defaultTierId', network.defaultTier.id);
            this.LocalNetworkProfile.set('tiers', network.tiers);
            this.LocalNetworkProfile.set('tagId', network.tagId);

            markFormSubmitSuccess(this.submitState, editForm);
            this._angularFormDirty = false;
            this._startTrackingForm();
            this.pending = false;
            this.eventBus.emit('SUBMIT_PROCESSED');
          });
        }, (response: any) => {
          this.ngZone.run(() => {
            this.handleSaveError(response, editForm);
          });
        });
      });
    }, (response: any) => {
      this.ngZone.run(() => {
        this.handleSaveError(response, editForm);
      });
    });
  }

  cancel(editForm: NgForm): void {
    if (this.isSaveDisabled(editForm)) {
      return;
    }
    this.initModels();
    this.submitState.validSubmission = false;
    this._angularFormDirty = false;
    editForm.control.markAsPristine();
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this._angularFormDirty;
  }

  isSaveDisabled(editForm: NgForm): boolean {
    return this.isFormPristine(editForm)
      || this.pending
      || (editForm.invalid && !hasFormValidationError(this.submitState));
  }

  markFormDirty(): void {
    this._registeredForm?.control.markAsDirty();
    this._angularFormDirty = true;
  }

  toggleAvailableCurrency(currency: string): void {
    const availableCurrencies = this.network.availableCurrencies || (this.network.availableCurrencies = []);
    const index = availableCurrencies.indexOf(currency);

    if (index >= 0) {
      availableCurrencies.splice(index, 1);
    } else {
      availableCurrencies.push(currency);
    }

    this.markFormDirty();
  }

  removeLocations(index: number): void {
    _.pullAt(this.networkProfile.profile.adnDefaults.namedLocations, index);
    _.pullAt(this.namedLocationRowIds, index);
    this.markFormDirty();
  }

  addLocations(): void {
    this.networkProfile.profile.adnDefaults.namedLocations.push('');
    this.namedLocationRowIds.push(this._nextNamedLocationRowId++);
    this.markFormDirty();
  }

  updateNamedLocation(index: number, countryCode: string): void {
    this.networkProfile.profile.adnDefaults.namedLocations[index] = countryCode;
    this.markFormDirty();
  }

  formatStat(value: any): string {
    return formatAdnStat(value, undefined, undefined, undefined, this.LocalNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  advancedLoggingHelpText(): string {
    const key = `network.advancedLogging.${this.network.advancedLogging}.helpText`;
    return this.translate.instant(key, {sftpHost: this.sftpHost});
  }

  rawLogTypesHelpText(): string {
    return this.translate.instant('network.rawLogTypes.helpText', {sftpHost: this.sftpHost});
  }

  compareById(option: any, value: any): boolean {
    return option && value ? option.id === value.id : option === value;
  }

  trackByNamedLocation = (index: number): number => {
    return this.namedLocationRowIds[index];
  };

  private handleSaveError(response: any, editForm: NgForm): void {
    if ((response.status === 400 || response.status === 403 || response.status === 422)
      && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
      handleApiValidationErrors(
        this.submitState,
        this.network,
        _.get(response, ['data', 'errors']) || response.data,
        this.formControlErrors,
        editForm
      );
    }
    markFormSubmitError(this.submitState, editForm);
    this.pending = false;
    this.eventBus.emit('SUBMIT_PROCESSED');
  }

  private initModels(): void {
    this.network = _.cloneDeep(this.originalNetwork);
    this.networkProfile = _.cloneDeep(this.originalNetworkProfile);
    this.networkProfile.profile = this.networkProfile.profile || {};
    this.networkProfile.profile.adnDefaults = this.networkProfile.profile.adnDefaults || {};
    this.LocalNetworkProfile.sanitiseProfileData(this.networkProfile.profile.adnDefaults);
    if (this.networkProfile.profile.adnDefaults.namedLocations.length === 0) {
      this.networkProfile.profile.adnDefaults.namedLocations.push('');
    }
    this.namedLocationRowIds = _.map(this.networkProfile.profile.adnDefaults.namedLocations, () => this._nextNamedLocationRowId++);
  }

  private loadSupportingData(): void {
    this.MarketplaceProduct.query({pageSize: 505, minimal: true}).$promise.then((data: any) => {
      this.ngZone.run(() => {
        this.marketplaceProducts = data.results;
      });
    });

    this.currenciesResource.get().then((currencyList: any[]) => {
      // [] → populated list flips *ngFor during the network-load CD turn; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.currencies = _.map(currencyList, 'currency');
      });
    });

    if (this.app === 'ADVERTISING') {
      this.tierResource.query().$promise.then((page: any) => {
        // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferMarkForCheck(this.cdr, () => {
          this.tiers = page.results;
          _.forEach(this.tiers, (tier) => {
            tier.displayName = tier.name + (_.find(this.network.tiers, {id: tier.id}) ? '' : ' (disabled)');
          });
        });
      });
    }
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    this._initializingForm = true;
    this._angularFormDirty = false;
    this._formSettleTimer = setTimeout(() => {
      if (!this._registeredForm) {
        return;
      }
      this._registeredForm.control.markAsPristine();
      this._registeredForm.control.markAsUntouched();
      this._angularFormDirty = false;
      this._initializingForm = false;
      this._formSub = this._registeredForm.valueChanges?.subscribe(() => {
        if (!this._initializingForm && !this._angularFormDirty) {
          this._angularFormDirty = true;
        }
      }) || null;
    }, 500);
  }
}
